import { type FC, useMemo } from "react";

import LogoSvg from "~/assets/icons/logo.svg?react";
import { Y_AXIS_WIDTH } from "~/feature/chart/constants";
import { useVolumeHoverArea } from "~/feature/chart/hooks/useVolumeHoverArea";
import { useCoreChart } from "~/feature/chart/providers/CoreChartProvider";
import { useVolumeHover } from "~/feature/chart/providers/VolumeHoverProvider";
import { getPointLeftPercent } from "~/feature/chart/utils";

import {
  AXIS_HIGHLIGHT,
  CHART_MARGIN,
  SLOT_PROPS,
  UNAVAILABLE_LOCALE_TEXT,
  X_AXIS_ID,
} from "./chartOptions";
import { CHART_HEIGHT } from "./constants";
import { formatCursorHint } from "./cursorHint";
import * as Styled from "./LinearChart.styles";
import { LinearChartSkeleton } from "./LinearChartSkeleton";
import { SplitLine } from "./SplitLine";
import { useChartLines } from "./useChartLines";

type AxisItems = { axisId: string | number; dataIndex: number }[];

const SLOTS = { line: SplitLine };

export const LinearChart: FC = () => {
  const {
    settings,
    timeline,
    isChanceHistoryLoading,
    isChanceHistoryUnavailable,
  } = useCoreChart();
  const { xAxis, yAxis, series, lineStyles } = useChartLines();
  const { hoveredIndex } = useVolumeHover();
  const { onMouseMove, onPointerDown, onMouseLeave } =
    useVolumeHoverArea(Y_AXIS_WIDTH);

  const hoveredTimestamp =
    hoveredIndex === null ? null : (timeline[hoveredIndex] ?? null);
  const cursorLeftPercent =
    hoveredIndex === null
      ? null
      : getPointLeftPercent(hoveredIndex, timeline.length);

  const localeText = isChanceHistoryUnavailable
    ? UNAVAILABLE_LOCALE_TEXT
    : undefined;

  const highlightedAxis = useMemo<AxisItems>(
    () =>
      hoveredIndex === null
        ? []
        : [{ axisId: X_AXIS_ID, dataIndex: hoveredIndex }],
    [hoveredIndex]
  );

  if (isChanceHistoryLoading) {
    return <LinearChartSkeleton />;
  }

  return (
    <Styled.Root>
      <Styled.Watermark aria-hidden>
        <LogoSvg style={Styled.watermarkSvgStyle} />
      </Styled.Watermark>

      <Styled.ChartArea
        onMouseMove={onMouseMove}
        onPointerDown={onPointerDown}
        onMouseLeave={onMouseLeave}
      >
        <Styled.Chart
          $showYAxis={settings.yAxis}
          $lineStyles={lineStyles}
          height={CHART_HEIGHT}
          series={series}
          grid={{
            horizontal: settings.horizontalGrid,
            vertical: settings.verticalGrid,
          }}
          axisHighlight={AXIS_HIGHLIGHT}
          highlightedAxis={highlightedAxis}
          xAxis={xAxis}
          yAxis={yAxis}
          margin={CHART_MARGIN}
          slots={SLOTS}
          slotProps={SLOT_PROPS}
          localeText={localeText}
          hideLegend
          skipAnimation
        />
      </Styled.ChartArea>

      {cursorLeftPercent !== null && hoveredTimestamp !== null && (
        <Styled.CursorHint $leftPercent={cursorLeftPercent}>
          {formatCursorHint(hoveredTimestamp)}
        </Styled.CursorHint>
      )}
    </Styled.Root>
  );
};
